<template>
  <v-container>
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        @click="$router.push('/marketing/campaigns')"
      >
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-skeleton-loader
        v-if="loading"
        type="heading"
        class="ml-4"
        width="260"
      />
      <template v-else>
        <h2 class="ml-2">
          {{ isNew ? 'New Campaign' : campaign.name }}
        </h2>
        <v-chip
          class="ml-3"
          small
          outlined
        >
          <v-icon
            small
            left
          >
            {{ typeInfo.icon }}
          </v-icon>
          {{ typeInfo.label }}
        </v-chip>
        <v-spacer />
        <v-chip
          v-if="!isNew"
          :color="statusFor(campaign.status).color"
          dark
        >
          {{ statusFor(campaign.status).label }}
        </v-chip>
        <!-- Scheduled campaigns only: pulls every unsent item back out of Approved so nothing goes out on the
             planned date. The date itself is kept — it's still a planned draft, just not a queued one. -->
        <v-btn
          v-if="canUnschedule"
          text
          small
          class="ml-2"
          :loading="unscheduling"
          @click="unschedule"
        >
          <v-icon
            small
            left
          >
            mdi-calendar-remove-outline
          </v-icon>
          Back to draft
        </v-btn>
        <!-- Draft only, matching the list: a scheduled or sent campaign is a record of work, not a draft to
             throw away. -->
        <v-btn
          v-if="!isNew && isDraft"
          icon
          class="ml-2"
          title="Delete campaign"
          :loading="deleting"
          @click="remove"
        >
          <v-icon>mdi-delete-outline</v-icon>
        </v-btn>
      </template>
    </div>

    <!-- Initial load: the store still holds the previously-viewed campaign, so render a skeleton rather
         than another campaign's name, copy and posts. The cards below are all gated on campaign.id, which
         is cleared for the duration of the load, so only the Plan card needs an explicit guard. -->
    <template v-if="loading">
      <v-skeleton-loader
        type="article, actions"
        class="mb-4"
      />
      <v-skeleton-loader type="article" />
    </template>

    <v-alert
      v-if="campaign.suggestionRationale"
      type="info"
      text
      border="left"
    >
      <strong>Why this campaign:</strong> {{ campaign.suggestionRationale }}
      <div v-if="campaign.suggestionAngle">
        <strong>Angle:</strong> {{ campaign.suggestionAngle }}
      </div>
    </v-alert>

    <!-- Plan -->
    <v-card
      v-if="!loading"
      class="mb-4"
    >
      <v-card-title>Plan</v-card-title>
      <v-card-text>
        <v-text-field
          v-model="campaign.name"
          label="Campaign name"
          outlined
          dense
        />
        <v-select
          v-model="campaign.objective"
          :items="objectiveItems"
          label="Objective"
          :hint="objectiveHintText"
          persistent-hint
          outlined
          dense
          clearable
          class="mb-4"
        >
          <template #item="{ item, on, attrs }">
            <v-list-item
              v-bind="attrs"
              v-on="on"
            >
              <v-list-item-content>
                <v-list-item-title>{{ item.text }}</v-list-item-title>
                <v-list-item-subtitle class="text-wrap">
                  {{ item.hint }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-select>
        <v-text-field
          v-model="relevantDate"
          label="Relevant date (send before this)"
          type="date"
          outlined
          dense
          clearable
        />
        <v-textarea
          v-model="campaign.goal"
          label="Goal"
          outlined
          dense
          rows="3"
        />
        <v-autocomplete
          v-model="selectedTargets"
          :items="groupedOptions"
          item-text="name"
          label="Targets"
          hint="What this campaign is about. Adding a design group alongside its audience narrows the template art to that collection — the email still reaches the whole audience."
          persistent-hint
          multiple
          chips
          deletable-chips
          return-object
          outlined
          dense
          class="mb-4"
        >
          <template #item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ item.targetTypeName }}</v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
        <v-combobox
          v-model="campaign.targetUrl"
          :items="targetUrls"
          label="Target URL"
          hint="The main link to drive users to. Optional — leave blank to use the most appropriate link per subject. Type any URL, or pick a content or catalog page."
          persistent-hint
          clearable
          outlined
          dense
        />
        <v-btn
          color="primary"
          :loading="saving"
          @click="save"
        >
          Save
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Suggestions -->
    <v-card
      v-if="campaign.id && (loadingSuggestions || suggestions.length)"
      class="mb-4"
    >
      <v-card-title>Suggestions</v-card-title>
      <v-card-text>
        <NextStepList
          ref="suggestionList"
          :steps="suggestions"
          :loading="loadingSuggestions"
          loading-text="Checking for suggestions…"
          hint="Optional next steps to strengthen this campaign's subjects. Each runs in the background."
          @run="runSuggestion"
        />
      </v-card-text>
    </v-card>

    <!-- Audience -->
    <v-card
      v-if="campaign.id && isEmail"
      class="mb-4"
    >
      <v-card-title>Audience</v-card-title>
      <v-card-text>
        <div
          v-if="loadingAudience"
          class="d-flex align-center"
        >
          <v-progress-circular
            indeterminate
            size="20"
            width="2"
            class="mr-2"
          />
          Estimating&hellip;
        </div>
        <template v-else-if="audience">
          <div
            v-if="audience.estimatedRecipients != null"
            class="mb-1"
          >
            <span class="text-h5">{{ audience.estimatedRecipients.toLocaleString() }}</span>
            <span class="ml-1">estimated recipient{{ audience.estimatedRecipients === 1 ? '' : 's' }}</span>
          </div>
          <div
            v-else
            class="mb-1"
          >
            No work-area filter &mdash; this sends to everyone (the broadcast's default audience). Either no
            industry/topic is selected, or the industry selected isn't a work area anyone can have.
          </div>
          <div class="text-caption text--secondary mb-3">
            Approximate: active users in the targeted work areas. Customer.io applies unsubscribes and
            emailability, so the delivered count will be lower.
          </div>
          <div v-if="audience.workAreas && audience.workAreas.length">
            <div class="text-caption mb-1">
              Work areas targeted
            </div>
            <v-chip
              v-for="area in audience.workAreas"
              :key="area"
              small
              class="mr-1 mb-1"
            >
              {{ area }}
            </v-chip>

            <!-- The exact recipients filter this campaign sends to Customer.io — hidden behind a toggle. -->
            <div
              v-if="audience.recipientsFilter"
              class="mt-2"
            >
              <v-btn
                text
                x-small
                class="px-1"
                @click="showRecipientsFilter = !showRecipientsFilter"
              >
                <v-icon left>
                  {{ showRecipientsFilter ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                {{ showRecipientsFilter ? 'Hide details' : 'View details' }}
              </v-btn>
              <div
                v-if="showRecipientsFilter"
                class="mt-1"
              >
                <div class="text-caption mb-1">
                  Sent to Customer.io
                </div>
                <pre class="cio-filter">{{ audience.recipientsFilter }}</pre>
              </div>
            </div>

            <!-- Authoritative count straight from Customer.io (heavier lookup, so on demand). -->
            <div class="mt-3 d-flex align-center flex-wrap">
              <v-btn
                small
                outlined
                :loading="loadingCioCount"
                @click="checkCioCount"
              >
                Check Customer.io count
              </v-btn>
              <span
                v-if="cioCount && !cioCount.error"
                class="ml-3"
              >
                <strong>{{ cioCount.count.toLocaleString() }}{{ cioCount.isPartial ? '+' : '' }}</strong>
                in Customer.io
              </span>
              <span
                v-else-if="cioCount && cioCount.error"
                class="ml-3 error--text"
              >
                {{ cioCount.error }}
              </span>
            </div>
          </div>
        </template>
        <div v-else>
          Audience unavailable.
        </div>
      </v-card-text>
    </v-card>

    <!--
      AI cost. The total rides on the campaign itself, so it renders immediately; the per-step breakdown is
      one more request and sits behind a toggle rather than on every page open. Hidden entirely when nothing
      has been recorded — a campaign generated before spend carried a campaign id has no rows to show, and an
      empty card would read as "this campaign was free".
    -->
    <v-card
      v-if="campaign.id && campaign.aiCalls"
      class="mb-4"
    >
      <v-card-title>
        AI cost
        <v-spacer />
        <span class="text-h6">{{ campaign.aiCost | currencyAi }}</span>
      </v-card-title>
      <v-card-text>
        <div class="d-flex align-center">
          <span class="grey--text text--darken-1">
            {{ campaign.aiCalls }} billed calls across every generation for this campaign, retries included.
          </span>
          <v-spacer />
          <v-btn
            text
            small
            :loading="spendLoading"
            @click="toggleSpend"
          >
            {{ spendOpen ? 'Hide breakdown' : 'Breakdown' }}
          </v-btn>
        </div>

        <v-simple-table
          v-if="spendOpen && spend"
          dense
          class="mt-3"
        >
          <thead>
            <tr>
              <th>Step</th>
              <th class="text-end">Calls</th>
              <th class="text-end">Tokens</th>
              <th class="text-end">Cost</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in spend.rows"
              :key="`${row.taskKey}_${row.refDetail}_${i}`"
            >
              <td>{{ spendStepLabel(row) }}</td>
              <td class="text-end">{{ row.calls }}</td>
              <td class="text-end">
                {{ row.inputTokens + row.outputTokens ? row.inputTokens + row.outputTokens : '—' }}
              </td>
              <td class="text-end">{{ row.cost | currencyAi }}</td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
    </v-card>

    <!-- Schedule -->
    <v-card
      v-if="campaign.id && isEmail && emailItem && emailContent"
      class="mb-4"
    >
      <v-card-title>Schedule</v-card-title>
      <v-card-text>
        <v-alert
          v-if="emailItem.errorMessage"
          type="error"
          text
          dense
        >
          {{ emailItem.errorMessage }}
        </v-alert>
        <div
          v-if="emailItem.sentDate"
          class="mb-2 success--text"
        >
          Sent {{ new Date(emailItem.sentDate).toLocaleString() }}
        </div>
        <v-row
          align="center"
          dense
        >
          <!-- Undated, the field has nothing to show and nothing to save, so it stays out of the way until the
               campaign has a date to edit. -->
          <v-col
            v-if="when"
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="when"
              label="Planned send (Central time)"
              type="datetime-local"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <!-- A hand-made campaign is created without a date, so the first press is "pick one for me" (the same
                 auto-scheduler audience plans and calendar fills use) rather than "save what I typed". -->
            <v-btn
              v-if="!when"
              color="primary"
              :loading="scheduling"
              @click="setDate()"
            >
              <v-icon left>
                mdi-calendar-check
              </v-icon>
              Set date
            </v-btn>
            <template v-else>
              <v-btn
                color="primary"
                :loading="savingDate"
                @click="saveDate"
              >
                Save date
              </v-btn>
              <!-- Hands the date back to the scheduler — for when the campaign has moved on from the day it was
                   given (its subject changed, or the week filled up around it). -->
              <v-btn
                v-if="canReschedule"
                text
                class="ml-2"
                :loading="scheduling"
                @click="setDate(true)"
              >
                Reset date
              </v-btn>
            </template>
            <v-btn
              text
              class="ml-2"
              :loading="sendingTest"
              @click="sendTest"
            >
              Send test to me
            </v-btn>
          </v-col>
        </v-row>
        <div
          v-if="!when"
          class="text-caption grey--text mt-2"
        >
          {{ setDateHint }}
        </div>
      </v-card-text>
    </v-card>

    <!-- Results — how the send actually performed. Customer.io pools every campaign under the one broadcast it
         triggers, so these come from the reporting events we store, matched on the trigger id it returned. -->
    <v-card
      v-if="campaign.id && isEmail && emailItem && emailItem.sentDate"
      class="mb-4"
    >
      <v-card-title>Results</v-card-title>
      <v-card-text>
        <v-btn
          small
          outlined
          :loading="loadingEmailStats"
          @click="checkEmailStats"
        >
          Check results
        </v-btn>

        <div
          v-if="emailStats && emailStats.error"
          class="mt-3 error--text"
        >
          {{ emailStats.error }}
        </div>

        <template v-else-if="emailStats">
          <div
            v-if="!emailStats.totalEvents"
            class="mt-3 text--secondary"
          >
            No reporting events have arrived for this send yet.
          </div>

          <template v-else>
            <v-row
              class="mt-1"
              dense
            >
              <v-col
                v-for="stat in emailStatTiles"
                :key="stat.label"
                cols="6"
                sm="4"
                md="2"
              >
                <div class="text-h6">
                  {{ stat.value }}
                </div>
                <div class="text-caption text--secondary">
                  {{ stat.label }}
                </div>
              </v-col>
            </v-row>

            <div
              v-if="emailStats.topLinks && emailStats.topLinks.length"
              class="mt-4"
            >
              <div class="text-subtitle-2 mb-1">
                Most clicked links
              </div>
              <div
                v-for="link in emailStats.topLinks"
                :key="link.url"
                class="text-caption d-flex"
              >
                <span class="text-truncate">{{ link.url }}</span>
                <span class="ml-2 font-weight-bold">{{ link.clicks.toLocaleString() }}</span>
              </div>
            </div>
          </template>
        </template>
      </v-card-text>
    </v-card>

    <!-- Email -->
    <v-card
      v-if="campaign.id && isEmail"
      class="mb-4"
    >
      <v-card-title>
        Email
        <v-spacer />
        <!-- Link check: an invented URL in generated copy is a 404 in the reader's inbox, so it has to be
             visible before the send. Click opens the full list — the count alone doesn't say WHICH link. -->
        <v-chip
          v-if="linkCheck && linkCheck.total"
          small
          :color="linkCheck.allValid ? 'success' : 'error'"
          dark
          class="mr-2"
          title="See every link and whether it resolves"
          @click="linkCheckDialog = true"
        >
          <v-icon
            left
            small
          >
            {{ linkCheck.allValid ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}
          </v-icon>
          {{ linkCheck.valid }}/{{ linkCheck.total }} links valid
        </v-chip>
        <v-chip
          v-if="emailItem"
          small
          :color="itemStatusFor(emailItem.status).color"
          dark
        >
          {{ itemStatusFor(emailItem.status).label }}
        </v-chip>
      </v-card-title>
      <v-card-text>
        <div class="mb-3">
          <v-btn
            color="secondary"
            :loading="generating"
            :disabled="!hasTargets"
            @click="onGenerateClick"
          >
            <v-icon left>
              mdi-robot-outline
            </v-icon>
            {{ emailContent ? 'Regenerate' : 'Generate email' }}
          </v-btn>
          <v-btn
            v-if="emailContent && emailItem && emailItem.status === itemStatusIds.DRAFT"
            color="primary"
            class="ml-2"
            @click="approve"
          >
            Approve
          </v-btn>
          <v-btn
            v-if="emailItem && emailItem.status === itemStatusIds.FAILED"
            color="primary"
            class="ml-2"
            :loading="retryingId === emailItem.id"
            @click="retry(emailItem)"
          >
            <v-icon left>
              mdi-refresh
            </v-icon>
            Retry send
          </v-btn>
          <v-btn
            v-if="emailItem && emailItem.status === itemStatusIds.FAILED"
            text
            class="ml-2"
            @click="startMarkSent(emailItem)"
          >
            <v-icon left>
              mdi-check
            </v-icon>
            Mark as sent
          </v-btn>
          <!-- Separate from the regenerate drawer on purpose: the inbox line gets iterated on far more than the
               email does, and this rewrites only it — the approved body and hero image are untouched. -->
          <v-btn
            v-if="canEditEmail && !editingEmail"
            text
            class="ml-2"
            @click="startSubjectRewrite"
          >
            <v-icon left>
              mdi-text-short
            </v-icon>
            Rewrite subject
          </v-btn>
          <v-btn
            v-if="canEditEmail && !editingEmail"
            text
            class="ml-2"
            @click="startEditEmail"
          >
            <v-icon left>
              mdi-pencil-outline
            </v-icon>
            Edit
          </v-btn>
          <div class="text-caption grey--text mt-1">
            {{
              emailContent
                ? 'Choose what to replace — the copy, the hero image, or both.'
                : 'Generates a fresh hero image and the email copy together.'
            }}
          </div>
        </div>

        <!-- Editing: the same fields as the read-only view, but writable, with the preview tracking the HTML live. -->
        <div v-if="emailContent && editingEmail">
          <v-text-field
            v-model="emailDraft.subject"
            label="Subject"
            outlined
            dense
          />
          <v-text-field
            v-model="emailDraft.preheader"
            label="Preheader"
            outlined
            dense
          />
          <v-textarea
            v-model="emailDraft.body_html"
            label="Email HTML"
            hint="Edited by hand — images are re-normalized on save so nothing renders distorted."
            persistent-hint
            outlined
            rows="14"
            spellcheck="false"
            class="email-html-editor mb-3"
          />
          <div class="text-caption grey--text mb-1">
            Preview
          </div>
          <iframe
            :srcdoc="emailDraft.body_html"
            class="email-preview mb-3"
          />
          <v-btn
            color="primary"
            :loading="savingEmail"
            :disabled="!emailDraft.body_html"
            @click="saveEmailContent"
          >
            Save email
          </v-btn>
          <v-btn
            text
            class="ml-2"
            :disabled="savingEmail"
            @click="editingEmail = false"
          >
            Cancel
          </v-btn>
        </div>

        <div v-else-if="emailContent">
          <div class="text-caption grey--text">
            Subject
          </div>
          <div class="text-subtitle-1 mb-2">
            {{ emailContent.subject }}
          </div>
          <div class="text-caption grey--text">
            Preheader
          </div>
          <div class="mb-3">
            {{ emailContent.preheader }}
          </div>
          <div class="text-caption grey--text mb-1">
            Preview
          </div>
          <iframe
            :srcdoc="emailContent.body_html"
            class="email-preview"
          />
        </div>
        <div
          v-else
          class="text-caption grey--text"
        >
          No email generated yet.
        </div>
      </v-card-text>
    </v-card>

    <!-- Social schedule -->
    <v-card
      v-if="campaign.id && isSocial && hasSocialContent"
      class="mb-4"
    >
      <v-card-title>Schedule</v-card-title>
      <v-card-text>
        <v-row
          align="center"
          dense
        >
          <!-- Hidden until there's a date to edit, as on the email card. -->
          <v-col
            v-if="when"
            cols="12"
            sm="6"
          >
            <v-text-field
              v-model="when"
              label="Planned send (Central time)"
              type="datetime-local"
              outlined
              dense
              hide-details
            />
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <!-- Same as email: undated until someone asks for a date (here every network takes the shared one). -->
            <v-btn
              v-if="!when"
              color="primary"
              :loading="scheduling"
              @click="setDate()"
            >
              <v-icon left>
                mdi-calendar-check
              </v-icon>
              Set date
            </v-btn>
            <template v-else>
              <v-btn
                color="primary"
                :loading="savingDate"
                @click="saveSocialDate"
              >
                Save date
              </v-btn>
              <!-- As on the email card: re-picks the day, every network moving together. -->
              <v-btn
                v-if="canReschedule"
                text
                class="ml-2"
                :loading="scheduling"
                @click="setDate(true)"
              >
                Reset date
              </v-btn>
            </template>
          </v-col>
        </v-row>
        <div
          v-if="!when"
          class="text-caption grey--text mt-2"
        >
          {{ setDateHint }}
        </div>
      </v-card-text>
    </v-card>

    <!-- Social -->
    <v-card
      v-if="campaign.id && isSocial"
      class="mb-4"
    >
      <v-card-title>Social posts</v-card-title>
      <v-card-text>
        <div class="mb-2 d-flex align-center flex-wrap">
          <v-btn
            color="secondary"
            :loading="generating"
            :disabled="!hasTargets || !platformOptions.length"
            @click="onGenerateSocialClick"
          >
            <v-icon left>
              mdi-robot-outline
            </v-icon>
            {{ hasSocialContent ? 'Regenerate all' : 'Generate posts' }}
          </v-btn>
          <v-btn
            v-if="hasSocialContent && hasDraftSocialItems"
            color="primary"
            class="ml-2"
            :loading="approving"
            @click="approveSocial"
          >
            Approve
          </v-btn>
          <v-btn
            v-if="failedSocialItems.length > 1"
            color="primary"
            class="ml-2"
            :loading="retryingId === 'all'"
            @click="retryAllSocial"
          >
            <v-icon left>
              mdi-refresh
            </v-icon>
            Retry {{ failedSocialItems.length }} failed
          </v-btn>
          <v-menu
            v-if="addablePlatforms.length"
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                text
                small
                class="ml-2"
                :loading="saving"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon
                  left
                  small
                >
                  mdi-plus
                </v-icon>
                Add network
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="p in addablePlatforms"
                :key="p.id"
                @click="addPlatform(p.id)"
              >
                <v-list-item-title>{{ p.name }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
        <div class="text-caption grey--text mb-3">
          <span v-if="!platformOptions.length">No networks are enabled — enable them in Send rate settings.</span>
          <span v-else>Generates tailored copy per network, with a small image gallery for Instagram &amp; Facebook.</span>
        </div>

        <div v-if="socialItems.length">
          <v-row dense>
            <v-col
              v-for="item in socialItems"
              :key="item.id"
              cols="12"
              md="6"
            >
              <v-card outlined>
                <v-card-title class="text-subtitle-1">
                  <PlatformIcon :platform="item.platform" />
                  <v-spacer />
                  <v-btn
                    icon
                    small
                    :disabled="generating"
                    title="Regenerate this post"
                    @click="regenerateItem(item)"
                  >
                    <v-icon small>
                      mdi-robot-outline
                    </v-icon>
                  </v-btn>
                  <v-btn
                    v-if="!item.sentDate"
                    icon
                    small
                    :disabled="saving"
                    title="Remove this network"
                    @click="removeSocialItem(item)"
                  >
                    <v-icon small>
                      mdi-trash-can-outline
                    </v-icon>
                  </v-btn>
                  <v-chip
                    small
                    :color="itemStatusFor(item.status).color"
                    dark
                    class="ml-1"
                  >
                    {{ itemStatusFor(item.status).label }}
                  </v-chip>
                </v-card-title>
                <v-card-text>
                  <div
                    v-if="socialImages(item).length"
                    class="mb-2"
                  >
                    <!-- Single image (e.g. Pinterest): show the whole image, uncropped. -->
                    <v-img
                      v-if="socialImages(item).length === 1"
                      :src="socialImages(item)[0]"
                      contain
                      max-height="360"
                      class="grey lighten-3 rounded"
                    />
                    <!-- IG/FB gallery: small thumbnails, still uncropped so nothing is cut off. -->
                    <div
                      v-else
                      class="d-flex"
                      style="gap: 6px; overflow-x: auto;"
                    >
                      <v-img
                        v-for="(img, idx) in socialImages(item)"
                        :key="idx"
                        :src="img"
                        width="110"
                        height="110"
                        contain
                        class="grey lighten-3 rounded flex-shrink-0"
                      />
                    </div>
                  </div>
                  <div class="mb-2">
                    {{ socialContent(item) ? socialContent(item).text : '' }}
                  </div>
                  <div class="text-caption primary--text">
                    {{ socialHashtags(item) }}
                  </div>
                  <div
                    v-if="item.platform === PLATFORM_X"
                    class="text-caption mt-1"
                    :class="charCount(item) > 280 ? 'error--text' : 'grey--text'"
                  >
                    {{ charCount(item) }} / 280 characters
                  </div>
                  <!-- Pinterest files a pin on a board, and which board decides who ever finds it — so show it
                       here rather than leaving it to be discovered after posting. -->
                  <div
                    v-if="item.platform === PLATFORM_PINTEREST && pinterest"
                    class="text-caption mt-2 d-flex align-center flex-wrap"
                  >
                    <v-icon
                      x-small
                      class="mr-1"
                    >
                      mdi-bulletin-board
                    </v-icon>
                    <span class="grey--text mr-1">Board:</span>
                    <span class="mr-2">{{ boardLabel }}</span>
                    <v-btn
                      v-if="!item.sentDate"
                      x-small
                      text
                      color="mcsprimary"
                      class="px-1"
                      :disabled="!pinterest.connected"
                      :title="pinterest.connected ? 'Send this pin to a different board' : 'Connect Pinterest first'"
                      @click="$refs.boardDialog.open(item.id, pinterest)"
                    >
                      Change
                    </v-btn>
                  </div>

                  <div
                    v-if="item.sentDate"
                    class="mt-2 success--text text-caption"
                  >
                    Posted {{ new Date(item.sentDate).toLocaleString() }}
                    <a
                      v-if="item.postUrl"
                      :href="item.postUrl"
                      target="_blank"
                      rel="noopener"
                      class="ml-1"
                    >View post</a>
                  </div>
                  <v-alert
                    v-if="item.errorMessage"
                    :type="item.status === itemStatusIds.SENT ? 'info' : 'error'"
                    text
                    dense
                    class="mt-2 mb-0"
                  >
                    {{ item.errorMessage }}
                  </v-alert>
                  <div v-if="item.status === itemStatusIds.FAILED">
                    <v-btn
                      small
                      color="primary"
                      class="mt-2"
                      :loading="retryingId === item.id"
                      @click="retry(item)"
                    >
                      <v-icon
                        left
                        small
                      >
                        mdi-refresh
                      </v-icon>
                      Retry send
                    </v-btn>
                    <v-btn
                      small
                      text
                      class="mt-2 ml-1"
                      @click="startMarkSent(item)"
                    >
                      <v-icon
                        left
                        small
                      >
                        mdi-check
                      </v-icon>
                      Mark as sent
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <div
          v-else
          class="text-caption grey--text"
        >
          No posts generated yet.
        </div>
      </v-card-text>
    </v-card>

    <!-- Regenerate with comments -->
    <v-navigation-drawer
      v-model="regenDrawer"
      right
      temporary
      fixed
      width="420"
    >
      <div class="pa-4">
        <h3 class="mb-2">
          {{ regenTitle }}
        </h3>
        <p class="text-caption grey--text mb-3">
          Pick what to replace, then add optional comments to steer it.
        </p>
        <v-radio-group
          v-model="regenScope"
          hide-details
          class="mt-0 mb-4"
        >
          <v-radio
            v-for="opt in regenScopeOptions"
            :key="opt.value"
            :value="opt.value"
            class="mb-2"
          >
            <template #label>
              <div>
                <div>{{ opt.label }}</div>
                <div class="text-caption grey--text">
                  {{ opt.hint }}
                </div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
        <v-checkbox
          v-if="regenScope !== $enums.MARKETING.REGENERATE_SCOPE.COPY_ONLY && hasCurrentRegenImage"
          v-model="regenUsePriorImage"
          label="Start from the current image"
          hint="Sends the current image to the AI and edits it, so the new one keeps its subject and composition. Leave off for a completely fresh render."
          persistent-hint
          dense
          class="mt-0 mb-4"
        />
        <v-checkbox
          v-if="regenScope !== $enums.MARKETING.REGENERATE_SCOPE.COPY_ONLY && !regenUsePriorImage"
          v-model="regenUseTemplateArt"
          label="Feature one of our templates"
          hint="Builds the image around a real published design for this campaign's subject. Turn off for imagery that doesn't show one of our templates."
          persistent-hint
          dense
          class="mt-0 mb-4"
        />
        <v-textarea
          v-if="regenScope !== $enums.MARKETING.REGENERATE_SCOPE.IMAGE_ONLY"
          v-model="regenComments"
          label="General Comments"
          hint="Steers the copy — tone, focus, what to change or keep."
          persistent-hint
          outlined
          rows="6"
          auto-grow
          class="mb-4"
        />
        <v-textarea
          v-if="regenScope !== $enums.MARKETING.REGENERATE_SCOPE.COPY_ONLY"
          v-model="regenImageComments"
          label="Image Comments"
          hint="Optional — steers the new image: subject, composition, colors, style."
          persistent-hint
          outlined
          rows="4"
          auto-grow
          class="mb-4"
        />
        <v-btn
          color="secondary"
          block
          :loading="generating"
          @click="regenerate"
        >
          <v-icon left>
            mdi-robot-outline
          </v-icon>
          Regenerate
        </v-btn>
      </div>
    </v-navigation-drawer>

    <!-- Rewrite the subject line + preheader only. Its own dialog, not a scope on the regenerate drawer: nothing
         here touches the body or the image, so none of those options apply. -->
    <v-dialog
      v-model="subjectDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h6">
          Rewrite subject &amp; preheader
        </v-card-title>
        <v-card-text>
          <p class="text-body-2">
            Writes a new subject line and preheader against the email as it stands. The body copy and hero image
            are left exactly as they are.
          </p>
          <div class="text-caption grey--text">
            Current subject
          </div>
          <div class="mb-2">
            {{ emailContent && emailContent.subject }}
          </div>
          <div class="text-caption grey--text">
            Current preheader
          </div>
          <div class="mb-3">
            {{ emailContent && emailContent.preheader }}
          </div>
          <v-textarea
            v-model="subjectComments"
            label="Subject Comments"
            hint="Optional — steers the two lines only: shorter, lead with the offer, drop the emoji."
            persistent-hint
            outlined
            rows="4"
            auto-grow
            autofocus
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="generatingSubject"
            @click="subjectDialog = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="secondary"
            :loading="generatingSubject"
            @click="regenerateSubject"
          >
            <v-icon left>
              mdi-robot-outline
            </v-icon>
            Rewrite
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <BoardDialog
      ref="boardDialog"
      @saved="loadPinterest"
    />

    <!-- Closing out a failed item that actually published. The external id is required, not optional: it is what
         attribution joins on, so a Sent row without one is a record that reads as real but can't be traced. -->
    <v-dialog
      v-model="markSentDialog"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          Mark as sent
        </v-card-title>
        <v-card-text>
          <p class="text-body-2">
            Use this only when the post really did publish and our record is wrong — a retry would double-post.
            Paste the id the network gave the published post.
          </p>
          <v-text-field
            v-model="markSentExternalId"
            label="External post id"
            hint="The Pinterest pin id, tweet id, or Customer.io trigger id"
            persistent-hint
            outlined
            dense
            autofocus
            :error-messages="markSentError"
          />
          <v-text-field
            v-model="markSentPostUrl"
            label="Post URL (optional)"
            outlined
            dense
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="markingSent"
            @click="cancelMarkSent"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="markingSent"
            :disabled="!markSentExternalId || !markSentExternalId.trim()"
            @click="confirmMarkSent"
          >
            Mark as sent
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Every href in the email, broken ones first: the chip says how many failed, this says which. The urls are
         links so the editor can confirm a "not found" with their own eyes before rewriting the copy. -->
    <v-dialog
      v-model="linkCheckDialog"
      max-width="640"
      scrollable
    >
      <v-card v-if="linkCheck">
        <v-card-title class="text-h6">
          Email links
          <v-spacer />
          <v-chip
            small
            :color="linkCheck.allValid ? 'success' : 'error'"
            dark
          >
            {{ linkCheck.valid }}/{{ linkCheck.total }} valid
          </v-chip>
        </v-card-title>
        <v-card-text style="max-height: 60vh;">
          <p class="text-body-2">
            Every link found in the email, checked against the live site. A link that doesn't resolve is a 404 in
            the reader's inbox — fix the copy, or regenerate, before this sends.
          </p>
          <v-list dense>
            <v-list-item
              v-for="(link, idx) in checkedLinks"
              :key="idx"
              class="px-0"
            >
              <v-list-item-icon class="mr-3">
                <v-icon :color="link.isValid ? 'success' : 'error'">
                  {{ link.isValid ? 'mdi-check-circle-outline' : 'mdi-close-circle-outline' }}
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <a
                  :href="link.url"
                  target="_blank"
                  rel="noopener"
                  class="text-body-2"
                  style="word-break: break-all;"
                >{{ link.url }}</a>
                <div
                  v-if="link.reason"
                  class="text-caption error--text"
                >
                  {{ link.reason }}
                </div>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="linkCheckDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import NextStepList from '@/components/common/NextStepList';
import PlatformIcon from '@/components/common/PlatformIcon';
import BoardDialog from '@/components/marketing/campaigns/BoardDialog';
import { campaignStatus, itemStatus, objectiveHint, platformName } from '@/helpers/marketing';

export default {
  name: 'CampaignDetail',
  components: { NextStepList, PlatformIcon, BoardDialog },
  data() {
    return {
      loading: true,
      saving: false,
      deleting: false,
      unscheduling: false,
      generating: false,
      approving: false,
      retryingId: null, // id of the item being retried, or 'all' for the whole failed batch
      markSentDialog: false,
      markSentItem: null,
      markSentExternalId: '',
      markSentPostUrl: '',
      markSentError: '',
      markingSent: false,
      savingDate: false,
      scheduling: false,
      sendingTest: false,
      audience: null,
      loadingAudience: false,
      showRecipientsFilter: false,
      cioCount: null,
      loadingCioCount: false,
      emailStats: null,
      loadingEmailStats: false,
      // AI spend breakdown: fetched the first time it's opened and then kept for the visit.
      spend: null,
      spendOpen: false,
      spendLoading: false,
      when: '',
      selectedTargets: [],
      selectedPlatforms: [],
      settings: {},
      suggestions: [],
      loadingSuggestions: false,
      linkCheck: null,
      linkCheckDialog: false,
      editingEmail: false,
      savingEmail: false,
      emailDraft: { subject: '', preheader: '', body_html: '' },
      subjectDialog: false,
      subjectComments: '',
      generatingSubject: false,
      regenDrawer: false,
      regenChannel: 1,
      regenPlatform: null,
      regenComments: '',
      regenImageComments: '',
      regenScope: 1, // MARKETING.REGENERATE_SCOPE.COPY_ONLY — image changes stay opt-in
      regenUsePriorImage: false,
      regenUseTemplateArt: true, // our own designs are the default subject of campaign art
      PLATFORM_X: 4,
      PLATFORM_PINTEREST: 1,
      pinterest: null
    };
  },
  computed: {
    ...mapGetters({
      campaign: 'campaigns/campaign',
      targetOptions: 'campaigns/targetOptions',
      // Content-page AND catalog-page URLs offered as autocomplete suggestions for the Target URL — a campaign
      // usually wants to land on a listing, not the page about it. Free text is still allowed.
      targetUrls: 'campaigns/targetUrls'
    }),
    isNew() {
      return !this.campaign || !this.campaign.id;
    },
    isDraft() {
      return this.campaign.status === this.$enums.MARKETING.CAMPAIGN_STATUS_IDS.DRAFT;
    },
    isSocial() {
      return this.campaign.type === this.$enums.MARKETING.CAMPAIGN_TYPE.SOCIAL;
    },
    isEmail() {
      return !this.isSocial;
    },
    typeInfo() {
      return this.isSocial
        ? { label: 'Social', icon: 'mdi-share-variant-outline' }
        : { label: 'Email', icon: 'mdi-email-outline' };
    },
    hasTargets() {
      return (this.campaign.targets || []).length > 0;
    },
    platformOptions() {
      return (this.settings.enabledPlatforms || []).map((id) => ({
        id,
        name: this.$enums.MARKETING.PLATFORM[id] || `#${id}`
      }));
    },
    // Enabled networks the campaign doesn't have a post for yet — offered by the "Add network" menu.
    addablePlatforms() {
      const present = new Set(this.socialItems.map((i) => i.platform));
      return this.platformOptions.filter((p) => !present.has(p.id));
    },
    regenTitle() {
      if (this.regenChannel !== this.$enums.MARKETING.CHANNEL.SOCIAL) return 'Regenerate email';
      return this.regenPlatform != null
        ? `Regenerate ${this.platformName(this.regenPlatform)} post`
        : 'Regenerate all posts';
    },
    // What the image options are talking about — one hero for email, per-network artwork for social.
    regenImageLabel() {
      if (this.regenChannel !== this.$enums.MARKETING.CHANNEL.SOCIAL) return 'Hero image';
      return this.regenPlatform != null ? 'Post image' : 'Post images';
    },
    // What the copy options are talking about.
    regenCopyLabel() {
      return this.regenChannel === this.$enums.MARKETING.CHANNEL.SOCIAL ? 'post copy' : 'email copy';
    },
    // Whether the thing being regenerated already has an image to start from ("edit this image" only
    // makes sense when there is one).
    hasCurrentRegenImage() {
      if (this.regenChannel !== this.$enums.MARKETING.CHANNEL.SOCIAL) {
        return !!(this.emailItem && this.emailItem.imageUrl);
      }
      const items =
        this.regenPlatform != null
          ? this.socialItems.filter((i) => i.platform === this.regenPlatform)
          : this.socialItems;
      return items.some((i) => !!i.imageUrl);
    },
    // The three regenerate scopes, captioned for whichever channel the drawer is scoped to.
    regenScopeOptions() {
      const scope = this.$enums.MARKETING.REGENERATE_SCOPE;
      const image = this.regenImageLabel.toLowerCase();
      return [
        {
          value: scope.COPY_ONLY,
          label: 'Copy only',
          hint: `Rewrite the ${this.regenCopyLabel} and keep the current ${image}.`
        },
        {
          value: scope.IMAGE_ONLY,
          label: 'Image only',
          hint: `New ${image}, keeping the approved copy exactly as it is.`
        },
        {
          value: scope.ALL,
          label: 'Everything',
          hint: `New ${image} and new ${this.regenCopyLabel}, generated together.`
        }
      ];
    },
    // An override wins; otherwise the subject's own board, flagged when it doesn't exist yet.
    boardLabel() {
      if (!this.pinterest) return '';
      if (this.pinterest.overrideBoardName) return this.pinterest.overrideBoardName;
      if (this.pinterest.overrideBoardId) return 'Custom board';

      const subject = this.pinterest.subjectBoard;
      if (!subject) return 'Default board';
      return subject.exists ? subject.name : `${subject.name} (created on first pin)`;
    },
    socialItems() {
      return (this.campaign.items || []).filter(
        (i) => i.channel === this.$enums.MARKETING.CHANNEL.SOCIAL
      );
    },
    firstSocialItem() {
      return this.socialItems[0] || null;
    },
    hasSocialContent() {
      return this.socialItems.some((i) => !!this.socialContent(i));
    },
    hasDraftSocialItems() {
      return this.socialItems.some((i) => i.status === this.itemStatusIds.DRAFT);
    },
    failedSocialItems() {
      return this.socialItems.filter((i) => i.status === this.itemStatusIds.FAILED);
    },
    itemStatusIds() {
      return this.$enums.MARKETING.ITEM_STATUS_IDS;
    },
    scheduleItem() {
      return this.isSocial ? this.firstSocialItem : this.emailItem;
    },
    // Only a scheduled campaign can go back to draft, and only while nothing has left the building — the server
    // refuses a partly-sent one, so don't offer the button there either.
    canUnschedule() {
      return (
        !this.isNew &&
        this.campaign.status === this.$enums.MARKETING.CAMPAIGN_STATUS_IDS.SCHEDULED &&
        !(this.campaign.items || []).some((i) => i.sentDate)
      );
    },
    // A sent campaign's date is history, not a plan — the server won't move it, so don't offer to.
    canReschedule() {
      return !!this.scheduleItem && !this.scheduleItem.sentDate;
    },
    // Shown while the campaign has no date, in place of the date field. Says what "Set date" will do, because the
    // button picks the date rather than opening a picker, and that the pick is not final.
    setDateHint() {
      const what = this.isSocial ? 'These posts have' : 'This email has';
      return `${what} no planned send yet — Set date picks the next opening that fits the send caps. You can ` +
        'change it afterwards.';
    },
    objectiveItems() {
      return Object.keys(this.$enums.MARKETING.OBJECTIVE).map((k) => ({
        value: Number(k),
        text: this.$enums.MARKETING.OBJECTIVE[k],
        hint: objectiveHint(k)
      }));
    },
    // Under the field: what the current pick does to the generated email. With nothing picked, say what the
    // choice is for rather than leaving the author guessing why the field matters.
    objectiveHintText() {
      return objectiveHint(this.campaign.objective) ||
        'Sets the intent behind the generated copy, and where its links point.';
    },
    groupedOptions() {
      return [...(this.targetOptions || [])].sort((a, b) => {
        if (a.targetTypeName !== b.targetTypeName) {
          return a.targetTypeName < b.targetTypeName ? -1 : 1;
        }
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
      });
    },
    relevantDate: {
      get() {
        return this.campaign.relevantDate ? this.campaign.relevantDate.slice(0, 10) : '';
      },
      set(value) {
        this.campaign.relevantDate = value || null;
      }
    },
    emailItem() {
      return (this.campaign.items || []).find(
        (i) => i.channel === this.$enums.MARKETING.CHANNEL.EMAIL
      );
    },
    // The Results tiles. Opens/clicks are UNIQUE people rather than raw events — one reader opening five times
    // is one interested reader, and the rates are computed on the same basis.
    emailStatTiles() {
      const s = this.emailStats;
      if (!s) return [];
      const pct = (rate) => (rate === null || rate === undefined ? '—' : `${(rate * 100).toFixed(1)}%`);
      return [
        { label: 'Delivered', value: (s.delivered || s.sent || 0).toLocaleString() },
        { label: `Opened (${pct(s.openRate)})`, value: (s.uniqueOpens || 0).toLocaleString() },
        { label: `Clicked (${pct(s.clickRate)})`, value: (s.uniqueClicks || 0).toLocaleString() },
        { label: 'Click-to-open', value: pct(s.clickToOpenRate) },
        { label: `Bounced (${pct(s.bounceRate)})`, value: (s.bounced || 0).toLocaleString() },
        { label: 'Unsubscribed', value: (s.unsubscribed || 0).toLocaleString() }
      ];
    },
    emailContent() {
      if (!this.emailItem || !this.emailItem.generatedJson) return null;
      try {
        return JSON.parse(this.emailItem.generatedJson);
      } catch (e) {
        return null;
      }
    },
    // A sent email is a historical record — everything before that is still editable by hand.
    canEditEmail() {
      return !!this.emailContent && !!this.emailItem && !this.emailItem.sentDate;
    },
    // Broken first: the reason the dialog was opened is at the top, without hunting through the valid ones.
    // Server order is preserved within each group, so the list still reads in the order the email links out.
    checkedLinks() {
      const links = (this.linkCheck && this.linkCheck.links) || [];
      return [...links.filter((l) => !l.isValid), ...links.filter((l) => l.isValid)];
    }
  },
  watch: {
    scheduleItem: {
      handler(item) {
        this.when = item && item.scheduledSend ? item.scheduledSend.slice(0, 16) : '';
      },
      immediate: true
    },
    // Re-check whenever the stored email changes — a generation, a hand edit, or the initial load.
    'emailItem.generatedJson': {
      handler() {
        this.loadLinkCheck();
      },
      immediate: true
    }
  },
  async mounted() {
    // The store keeps whichever campaign was viewed last; drop it now so nothing of the previous one is
    // on screen while this one loads.
    this.$store.commit('campaigns/SET_CAMPAIGN', {});
    this.loading = true;
    try {
      await this.loadCampaign();
    } finally {
      this.loading = false;
    }
    this.initTargets();
    this.initSelectedPlatforms();
    this.loadSuggestions();
    this.loadAudience();
    this.loadPinterest();
  },
  methods: {
    // Loads the per-step breakdown the first time it's asked for, then just toggles. Kept off the initial
    // page load: the total is already on the campaign, and most visits never open this.
    toggleSpend() {
      if (this.spend) {
        this.spendOpen = !this.spendOpen;
        return;
      }

      this.spendLoading = true;
      this.$store
        .dispatch('campaigns/getAiSpend', this.campaign.id)
        .then((result) => {
          this.spend = result || { rows: [] };
          this.spendOpen = true;
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.spendLoading = false;
        });
    },
    // What a breakdown row is. The task key says which generator ran and refDetail which part of the
    // campaign it ran for; where they'd say the same thing twice ("Email copy" for campaign.email/email)
    // the detail alone is the readable half. Unknown keys fall through to the raw value so a generator
    // added later still lists rather than rendering blank.
    spendStepLabel(row) {
      const labels = {
        suggest: 'Campaign suggestion',
        email: 'Email copy',
        subject: 'Subject line',
        social: 'Social copy',
        ads_copy: 'Ad copy',
        email_image: 'Email image',
        social_image: 'Social images',
        ad_image: 'Ad images',
        image: 'Imagery'
      };
      return labels[row.refDetail] || row.refDetail || row.taskKey;
    },
    // Everything the page needs before it can render: the target picker's options, the send settings and
    // the campaign itself (or a blank one for the /0 "new" route).
    async loadCampaign() {
      await this.$store.dispatch('campaigns/getTargetOptions').catch((error) => this.logError(error));
      // Load the Target URL suggestions (best-effort — free text still works without them).
      this.$store.dispatch('campaigns/getTargetUrls').catch((error) => this.logError(error));
      this.settings = await this.$store
        .dispatch('campaigns/getSettings')
        .catch((error) => this.logError(error)) || {};
      const id = this.$route.params.id;
      if (id && id !== '0') {
        await this.$store.dispatch('campaigns/get', id).catch((error) => this.logError(error));
        return;
      }
      const queryType = Number(this.$route.query.type) || this.$enums.MARKETING.CAMPAIGN_TYPE.EMAIL;
      // An audience can seed the subject (…?targetType=2&targetId=15), so "Campaign" from the audience
      // page lands on an editor that's already pointed at it.
      const targetType = Number(this.$route.query.targetType) || 0;
      const targetId = Number(this.$route.query.targetId) || 0;
      this.$store.commit('campaigns/SET_CAMPAIGN', {
        name: '',
        goal: '',
        objective: null,
        relevantDate: null,
        targetUrl: '',
        type: queryType,
        targets: targetType && targetId ? [{ targetType, targetId }] : [],
        selectedPlatforms: [],
        items: []
      });
    },
    // Email audience preview: the work areas the broadcast targets + a local (approximate) recipient estimate.
    // Loaded async and depends only on the campaign's Industry/Topic subjects, so it refreshes after a save.
    loadAudience() {
      if (!this.campaign.id || !this.isEmail) return;
      // The Customer.io count is filter-specific, so a changed audience invalidates any prior count.
      this.cioCount = null;
      // Same reasoning for the results: a stale figure must not survive a reload of the campaign.
      this.emailStats = null;
      this.showRecipientsFilter = false;
      this.loadingAudience = true;
      this.$store
        .dispatch('campaigns/getAudience', this.campaign.id)
        .then((data) => {
          this.audience = data || null;
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loadingAudience = false;
        });
    },
    // Which board this campaign's pin will file on. Only loaded for social campaigns that include Pinterest,
    // and best-effort: a Pinterest outage must not stop the rest of the page rendering.
    loadPinterest() {
      if (!this.campaign.id || this.isEmail) return;
      if (!this.socialItems.some((i) => i.platform === this.PLATFORM_PINTEREST)) return;

      this.$store
        .dispatch('campaigns/getPinterestTarget', this.campaign.id)
        .then((data) => {
          this.pinterest = data || null;
        })
        .catch(() => {
          this.pinterest = null;
        });
    },
    // How the send performed, from the stored Customer.io reporting events. Behind a button for the same reason
    // the audience count is: it's a deliberate lookup, not something the page needs on open.
    checkEmailStats() {
      if (!this.campaign.id) return;
      this.loadingEmailStats = true;
      this.emailStats = null;
      this.$store
        .dispatch('campaigns/getEmailStats', this.campaign.id)
        .then((data) => {
          this.emailStats = data || null;
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loadingEmailStats = false;
        });
    },
    // Authoritative audience size from Customer.io — an explicit, heavier lookup, so it's behind a button.
    checkCioCount() {
      if (!this.campaign.id) return;
      this.loadingCioCount = true;
      this.cioCount = null;
      this.$store
        .dispatch('campaigns/getAudienceCount', this.campaign.id)
        .then((data) => {
          this.cioCount = data || null;
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loadingCioCount = false;
        });
    },
    // Contextual next steps (e.g. create a missing landing page). Loaded async so it never blocks the page.
    loadSuggestions() {
      if (!this.campaign.id) return;
      this.loadingSuggestions = true;
      this.$store
        .dispatch('campaigns/getCampaignSuggestions', this.campaign.id)
        .then((data) => {
          this.suggestions = data || [];
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.loadingSuggestions = false;
        });
    },
    // NextStepList has already handled "link" steps and marked this one started; we only dispatch.
    runSuggestion(s) {
      this.$store
        .dispatch('campaigns/runSuggestion', { campaignId: this.campaign.id, key: s.key })
        .then(() => this.$root.$snackbarSuccess('Started — this runs in the background.'))
        .catch((error) => {
          this.$refs.suggestionList.unfire(s.key);
          this.logError(error);
        });
    },
    initTargets() {
      const options = this.targetOptions || [];
      this.selectedTargets = (this.campaign.targets || [])
        .map((t) =>
          options.find((o) => o.targetType === t.targetType && o.id === t.targetId)
        )
        .filter((o) => !!o);
    },
    initSelectedPlatforms() {
      // Existing campaigns keep their chosen networks; a fresh social campaign defaults to every enabled network
      // (the "Networks" picker was removed — networks are pruned per-post and re-added via the Add-network menu).
      const fromCampaign = this.campaign.selectedPlatforms || [];
      this.selectedPlatforms = fromCampaign.length
        ? [...fromCampaign]
        : (this.settings.enabledPlatforms || []).slice();
    },
    platformName,
    socialContent(item) {
      if (!item || !item.generatedJson) return null;
      try {
        return JSON.parse(item.generatedJson);
      } catch (e) {
        return null;
      }
    },
    socialHashtags(item) {
      const content = this.socialContent(item);
      if (!content || !content.hashtags) return '';
      return Array.isArray(content.hashtags) ? content.hashtags.join(' ') : content.hashtags;
    },
    socialImages(item) {
      const content = this.socialContent(item);
      const imgs = content && Array.isArray(content.images) ? content.images.filter(Boolean) : [];
      if (imgs.length) return imgs;
      return item.imageUrl ? [item.imageUrl] : [];
    },
    // Drops one network from the campaign (removes its unsent post) by saving the reduced platform set.
    removeSocialItem(item) {
      this.selectedPlatforms = this.selectedPlatforms.filter((p) => p !== item.platform);
      this.save();
    },
    // Adds a network via the Add-network menu: save creates its draft item, then we generate that one
    // network's post (copy + image) so adding a network actually produces a post. Generation needs targets;
    // without them we just add the draft (the Generate button is likewise disabled until targets exist).
    addPlatform(id) {
      if (!this.selectedPlatforms.includes(id)) this.selectedPlatforms = [...this.selectedPlatforms, id];
      this.saving = true;
      this.$store
        .dispatch('campaigns/save', this.buildCampaign())
        .then((saved) => {
          this.initTargets();
          this.initSelectedPlatforms();
          const id2 = (saved && saved.id) || this.campaign.id;
          if (this.isNewRoute() && id2) {
            this.$router.replace(`/marketing/campaigns/${id2}`);
          }
          if (!this.hasTargets || !id2) return null;
          // Generate just this network's post so the newly added network isn't left empty.
          this.generating = true;
          return this.$store
            .dispatch('campaigns/generateSocialItem', {
              id: id2,
              platform: id,
              comments: '',
              imageComments: '',
              scope: this.$enums.MARKETING.REGENERATE_SCOPE.ALL,
              usePriorImage: false
            })
            .then(() => this.$root.$snackbarSuccess(`${this.platformName(id)} post generated`))
            .finally(() => {
              this.generating = false;
            });
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.saving = false;
        });
    },
    // Opens the comments drawer scoped to a single network's post.
    regenerateItem(item) {
      this.regenChannel = this.$enums.MARKETING.CHANNEL.SOCIAL;
      this.regenPlatform = item.platform;
      this.resetRegenOptions();
      this.regenDrawer = true;
    },
    // Every drawer open starts from a clean slate: copy only, fresh render on our template art, no leftover comments.
    resetRegenOptions() {
      this.regenComments = '';
      this.regenImageComments = '';
      this.regenScope = this.$enums.MARKETING.REGENERATE_SCOPE.COPY_ONLY;
      this.regenUsePriorImage = false;
      this.regenUseTemplateArt = true;
    },
    charCount(item) {
      const content = this.socialContent(item);
      if (!content) return 0;
      return `${content.text || ''} ${this.socialHashtags(item)}`.trim().length;
    },
    statusFor: campaignStatus,
    itemStatusFor: itemStatus,
    isNewRoute() {
      const id = this.$route.params.id;
      return !id || id === '0';
    },
    buildCampaign() {
      return {
        ...this.campaign,
        type: this.campaign.type,
        selectedPlatforms: this.selectedPlatforms,
        targets: this.selectedTargets.map((opt) => ({
          targetType: opt.targetType,
          targetId: opt.id
        }))
      };
    },
    save() {
      this.saving = true;
      this.$store
        .dispatch('campaigns/save', this.buildCampaign())
        .then((saved) => {
          this.initTargets();
          this.initSelectedPlatforms();
          if (this.isNewRoute() && saved && saved.id) {
            this.$router.replace(`/marketing/campaigns/${saved.id}`);
          }
          // Subjects may have changed, so the targeted work areas / estimate can too.
          this.loadAudience();
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.saving = false;
        });
    },
    async remove() {
      const confirmed = await this.$root.$confirm(
        'Delete Campaign',
        'This cancels any send that has not gone out yet. If an audience plan created this campaign, that ' +
          'item goes back to Suggested so it can be built again. Are you sure?',
        { color: 'error' }
      );
      if (!confirmed) return;

      this.deleting = true;
      this.$store
        .dispatch('campaigns/remove', this.campaign.id)
        .then(() => {
          // The store clears the detail slot, so hide the body before it re-renders against an empty campaign.
          this.loading = true;
          this.$root.$snackbarSuccess('Campaign deleted');
          this.$router.push('/marketing/campaigns');
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.deleting = false;
        });
    },
    onGenerateClick() {
      // First generation is direct; a regenerate opens the options drawer.
      this.regenChannel = this.$enums.MARKETING.CHANNEL.EMAIL;
      this.regenPlatform = null;
      if (this.emailContent) {
        this.resetRegenOptions();
        this.regenDrawer = true;
      } else {
        // Nothing exists yet, so the first pass always makes both the image and the copy.
        this.generate({ scope: this.$enums.MARKETING.REGENERATE_SCOPE.ALL });
      }
    },
    onGenerateSocialClick() {
      this.regenChannel = this.$enums.MARKETING.CHANNEL.SOCIAL;
      this.regenPlatform = null; // the top button regenerates every network
      if (this.hasSocialContent) {
        this.resetRegenOptions();
        this.regenDrawer = true;
      } else {
        this.generate({ scope: this.$enums.MARKETING.REGENERATE_SCOPE.ALL });
      }
    },
    // Opens the subject-only dialog with a clean comment box.
    startSubjectRewrite() {
      this.subjectComments = '';
      this.subjectDialog = true;
    },
    // Subject + preheader only — the body HTML and hero image are untouched, so this shares nothing with generate().
    regenerateSubject() {
      this.generatingSubject = true;
      this.$store
        .dispatch('campaigns/generateEmailSubject', {
          id: this.campaign.id,
          comments: this.subjectComments || ''
        })
        .then(() => {
          // The rewrite replaces content the editor may be holding, so drop the now-stale draft.
          this.editingEmail = false;
          this.subjectDialog = false;
          this.subjectComments = '';
          this.$root.$snackbarSuccess('Subject and preheader updated');
        })
        .catch((error) => {
          const data = error && error.response && error.response.data;
          const msg =
            typeof data === 'string'
              ? data
              : (data && data.message) || 'Could not rewrite the subject line. Please try again.';
          this.$root.$snackbarError(msg);
        })
        .finally(() => {
          this.generatingSubject = false;
        });
    },
    regenerate() {
      this.generate({
        comments: this.regenComments,
        imageComments: this.regenImageComments,
        scope: this.regenScope,
        usePriorImage: this.regenUsePriorImage,
        useTemplateArt: this.regenUseTemplateArt
      });
    },
    generate({ comments, imageComments, scope, usePriorImage, useTemplateArt }) {
      const SCOPE = this.$enums.MARKETING.REGENERATE_SCOPE;
      const isSocial = this.regenChannel === this.$enums.MARKETING.CHANNEL.SOCIAL;
      const single = isSocial && this.regenPlatform != null;
      const action = single
        ? 'campaigns/generateSocialItem'
        : isSocial
          ? 'campaigns/generateSocial'
          : 'campaigns/generateEmail';
      const payload = {
        id: this.campaign.id,
        // Only send the comments the chosen scope actually uses, so a hidden textarea can't leak a stale value.
        comments: scope === SCOPE.IMAGE_ONLY ? '' : comments || '',
        imageComments: scope === SCOPE.COPY_ONLY ? '' : imageComments || '',
        scope: scope == null ? SCOPE.ALL : scope,
        usePriorImage: scope !== SCOPE.COPY_ONLY && !!usePriorImage,
        // Defaults on: campaign art normally features one of our published designs. Only meaningful for a
        // from-scratch render — editing the current image never attaches template art.
        useTemplateArt: useTemplateArt !== false
      };
      if (single) payload.platform = this.regenPlatform;
      const thing = isSocial ? 'posts' : 'email';
      this.generating = true;
      this.$store
        .dispatch(action, payload)
        .then(() => {
          // A regeneration replaces the content the editor was holding, so drop the now-stale draft.
          this.editingEmail = false;
          this.regenDrawer = false;
          this.resetRegenOptions();
          this.regenPlatform = null;
          const updated =
            payload.scope === SCOPE.IMAGE_ONLY
              ? isSocial ? 'Post images updated' : 'Hero image updated'
              : isSocial ? 'Posts updated' : 'Email updated';
          this.$root.$snackbarSuccess(updated);
        })
        .catch((error) => {
          const data = error && error.response && error.response.data;
          const msg =
            typeof data === 'string'
              ? data
              : (data && data.message) || `Could not regenerate the ${thing}. Please try again.`;
          this.$root.$snackbarError(msg);
        })
        .finally(() => {
          this.generating = false;
        });
    },
    // Best-effort: a link-check failure just hides the indicator rather than shouting at the editor.
    loadLinkCheck() {
      if (!this.emailItem || !this.emailItem.generatedJson) {
        this.linkCheck = null;
        return;
      }
      this.$store
        .dispatch('campaigns/checkLinks', this.emailItem.id)
        .then((data) => {
          this.linkCheck = data || null;
        })
        .catch(() => {
          this.linkCheck = null;
        });
    },
    startEditEmail() {
      const content = this.emailContent || {};
      this.emailDraft = {
        subject: content.subject || '',
        preheader: content.preheader || '',
        body_html: content.body_html || ''
      };
      this.editingEmail = true;
    },
    saveEmailContent() {
      this.savingEmail = true;
      this.$store
        .dispatch('campaigns/saveEmailContent', {
          itemId: this.emailItem.id,
          subject: this.emailDraft.subject,
          preheader: this.emailDraft.preheader,
          bodyHtml: this.emailDraft.body_html
        })
        .then(() => {
          this.editingEmail = false;
          this.$root.$snackbarSuccess('Email saved');
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.savingEmail = false;
        });
    },
    approve() {
      this.$store
        .dispatch('campaigns/approveItem', this.emailItem.id)
        .then(() => this.$store.dispatch('campaigns/get', this.campaign.id))
        .catch((error) => this.logError(error));
    },
    // Re-queues a failed item as-is — it goes out on the next dispatcher tick, not on a new date.
    retry(item) {
      this.retryingId = item.id;
      this.$store
        .dispatch('campaigns/retryItem', item.id)
        .then(() => this.reload())
        .then(() => this.$root.$snackbarSuccess('Queued for another send.'))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.retryingId = null;
        });
    },
    // The counterpart to retry(): the send already happened, so record it rather than repeat it.
    startMarkSent(item) {
      this.markSentItem = item;
      this.markSentExternalId = '';
      this.markSentPostUrl = item.postUrl || '';
      this.markSentError = '';
      this.markSentDialog = true;
    },
    cancelMarkSent() {
      this.markSentDialog = false;
      this.markSentItem = null;
    },
    confirmMarkSent() {
      this.markingSent = true;
      this.markSentError = '';
      this.$store
        .dispatch('campaigns/markItemSent', {
          itemId: this.markSentItem.id,
          externalId: this.markSentExternalId.trim(),
          postUrl: this.markSentPostUrl ? this.markSentPostUrl.trim() : null
        })
        .then(() => {
          this.markSentDialog = false;
          this.markSentItem = null;
          this.$root.$snackbarSuccess('Marked as sent.');
        })
        .catch((error) => {
          // Shown inline under the field, so suppress logError's alert — the dialog is still open behind it.
          const data = error?.response?.data;
          this.markSentError = data?.Message || data?.message || 'Could not mark this item as sent.';
          this.logError(error, true);
        })
        .finally(() => {
          this.markingSent = false;
        });
    },
    retryAllSocial() {
      this.retryingId = 'all';
      const failed = this.failedSocialItems;
      Promise.all(failed.map((i) => this.$store.dispatch('campaigns/retryItem', i.id)))
        .then(() => this.reload())
        .then(() => this.$root.$snackbarSuccess(`${failed.length} posts queued for another send.`))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.retryingId = null;
        });
    },
    // Un-approves every unsent item so the dispatcher stops seeing the campaign; the planned date stays put.
    async unschedule() {
      const confirmed = await this.$root.$confirm(
        'Back to draft?',
        'Nothing will send on the planned date until the campaign is approved again. The date is kept — use ' +
          '"Reset date" to move it.'
      );
      if (!confirmed) return;

      this.unscheduling = true;
      this.$store
        .dispatch('campaigns/unschedule', this.campaign.id)
        .then(() => this.$root.$snackbarSuccess('Back to draft'))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.unscheduling = false;
        });
    },
    // Lets the scheduler choose, for a campaign created without a date. Same pass an audience-plan or filled
    // campaign gets at creation, so the date it lands on respects the same weekly/daily caps. With `reset` it
    // re-picks a date the campaign already has (the server drops the old one first, so its own slot is free).
    setDate(reset = false) {
      this.scheduling = true;
      this.$store
        .dispatch('campaigns/scheduleCampaign', { id: this.campaign.id, reset })
        .then((campaign) => {
          // Read the date off the response rather than `when`, which the scheduleItem watcher hasn't refreshed yet.
          const send = ((campaign && campaign.items) || [])
            .map((i) => i.scheduledSend)
            .filter(Boolean)[0];
          this.$root.$snackbarSuccess(
            send ? `Scheduled for ${new Date(send).toLocaleString()}` : 'Scheduled.'
          );
        })
        .catch((error) => this.logError(error))
        .finally(() => {
          this.scheduling = false;
        });
    },
    saveDate() {
      this.savingDate = true;
      this.$store
        .dispatch('campaigns/setSendDate', { itemId: this.emailItem.id, when: this.when })
        .then(() => this.$store.dispatch('campaigns/get', this.campaign.id))
        .catch((error) => this.logError(error))
        .finally(() => {
          this.savingDate = false;
        });
    },
    sendTest() {
      this.sendingTest = true;
      this.$store
        .dispatch('campaigns/sendTest', this.campaign.id)
        .then((result) =>
          this.$root.$snackbarSuccess(
            result && result.email
              ? `Test email sent to ${result.email}`
              : 'Test email sent'
          )
        )
        .catch((error) => this.logError(error))
        .finally(() => {
          this.sendingTest = false;
        });
    },
    reload() {
      return this.$store.dispatch('campaigns/get', this.campaign.id);
    },
    approveSocial() {
      this.approving = true;
      const drafts = this.socialItems.filter((i) => i.status === 1);
      Promise.all(drafts.map((i) => this.$store.dispatch('campaigns/approveItem', i.id)))
        .then(() => this.reload())
        .catch((error) => this.logError(error))
        .finally(() => {
          this.approving = false;
        });
    },
    saveSocialDate() {
      this.savingDate = true;
      Promise.all(
        this.socialItems.map((i) =>
          this.$store.dispatch('campaigns/setSendDate', { itemId: i.id, when: this.when })
        )
      )
        .then(() => this.reload())
        .catch((error) => this.logError(error))
        .finally(() => {
          this.savingDate = false;
        });
    }
  }
};
</script>

<style scoped>
.email-preview {
  width: 100%;
  height: 500px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fff;
}
/* Raw HTML reads far better monospaced, and unwrapped lines stay legible. */
.email-html-editor >>> textarea {
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
  overflow-x: auto;
}
.cio-filter {
  margin: 0;
  padding: 8px 10px;
  max-height: 220px;
  overflow: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #f5f5f5;
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
